import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  Badge,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Tv as TvIcon,
  Radio as RadioIcon,
  VideoLibrary as VideoLibraryIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { apiService, Channel, Show } from '../services/api';
import Breadcrumb from './Breadcrumb';

const ChannelDetail: React.FC = () => {
  const navigate = useNavigate();
  const { channelId } = useParams<{ channelId: string }>();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [shows, setShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<Channel | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    status: 'draft' as 'active' | 'inactive' | 'draft' | 'archived',
    frequency: undefined as 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom' | undefined,
    timezone: 'UTC',
  });
  
  // Show creation dialog state
  const [openShowDialog, setOpenShowDialog] = useState(false);
  const [showFormData, setShowFormData] = useState({
    name: '',
    description: '',
    category: '',
    status: 'draft' as 'active' | 'inactive' | 'draft' | 'archived',
    frequency: 'daily' as 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom',
    timezone: 'UTC',
    newsCriteria: {},
    panelistIds: [] as string[],
    discussionSettings: {},
    isPublic: true,
    tags: [] as string[],
  });

  useEffect(() => {
    if (channelId) {
      fetchData();
    }
  }, [channelId]);

  const fetchData = async () => {
    if (!channelId) return;
    
    try {
      setLoading(true);
      const [channelData, showsData] = await Promise.all([
        apiService.getChannel(channelId),
        apiService.getShows(),
      ]);
      setChannel(channelData);
      setShows(showsData.filter(show => show.channelId === channelId));
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item: Channel) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      category: item.category || '',
      status: item.status,
      frequency: item.frequency,
      timezone: item.timezone || 'UTC',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      status: 'draft',
      frequency: undefined,
      timezone: 'UTC',
    });
  };

  const handleOpenShowDialog = () => {
    setShowFormData({
      name: '',
      description: '',
      category: '',
      status: 'draft',
      frequency: 'daily',
      timezone: 'UTC',
      newsCriteria: {},
      panelistIds: [],
      discussionSettings: {},
      isPublic: true,
      tags: [],
    });
    setOpenShowDialog(true);
  };

  const handleCloseShowDialog = () => {
    setOpenShowDialog(false);
    setShowFormData({
      name: '',
      description: '',
      category: '',
      status: 'draft',
      frequency: 'daily',
      timezone: 'UTC',
      newsCriteria: {},
      panelistIds: [],
      discussionSettings: {},
      isPublic: true,
      tags: [],
    });
  };

  const handleCreateShow = async () => {
    if (!channelId) return;
    
    try {
      const showData = {
        ...showFormData,
        channelId: channelId,
      };
      
      await apiService.createShow(showData);
      handleCloseShowDialog();
      fetchData(); // Refresh the shows list
    } catch (error) {
      console.error('Error creating show:', error);
      setError('Failed to create show');
    }
  };

  const handleSubmit = async () => {
    if (!editingItem) return;
    
    try {
      await apiService.updateChannel(editingItem._id, formData);
      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Error updating channel:', error);
      setError('Failed to update channel');
    }
  };

  const handleDelete = async () => {
    if (!channel) return;
    
    if (window.confirm('Are you sure you want to delete this channel? This will also delete all associated shows and episodes.')) {
      try {
        await apiService.deleteChannel(channel._id);
        navigate('/channels');
      } catch (error) {
        console.error('Error deleting channel:', error);
        setError('Failed to delete channel');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'draft': return 'warning';
      case 'archived': return 'default';
      case 'published': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box>
        <Breadcrumb items={[
          { label: 'Channels', path: '/channels', icon: <TvIcon /> },
          { label: 'Loading...', path: `/channels/${channelId}`, icon: <TvIcon /> }
        ]} />
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Breadcrumb items={[
          { label: 'Channels', path: '/channels', icon: <TvIcon /> },
          { label: 'Channel', path: `/channels/${channelId}`, icon: <TvIcon /> }
        ]} />
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      </Box>
    );
  }

  if (!channel) {
    return (
      <Box>
        <Breadcrumb items={[
          { label: 'Channels', path: '/channels', icon: <TvIcon /> },
          { label: 'Channel Not Found', path: `/channels/${channelId}`, icon: <TvIcon /> }
        ]} />
        <Alert severity="error" sx={{ m: 2 }}>Channel not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumb items={[
        { label: 'Channels', path: '/channels', icon: <TvIcon /> },
        { label: channel.name, path: `/channels/${channelId}`, icon: <TvIcon /> }
      ]} />
      
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1">
              {channel.name}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => handleOpenDialog(channel)}
              sx={{ mr: 2 }}
            >
              Edit Channel
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate(`/channels/${channelId}`)}
              startIcon={<RadioIcon />}
            >
              View Shows ({shows.length})
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Main Content Area */}
          <Grid item xs={12} lg={8}>
            {/* Channel Information */}
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Channel Information
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Chip 
                    label={channel.status} 
                    color={getStatusColor(channel.status) as any}
                    size="small"
                  />
                  {channel.frequency && (
                    <Chip label={channel.frequency} variant="outlined" size="small" />
                  )}
                  {channel.category && (
                    <Chip label={channel.category} variant="outlined" size="small" />
                  )}
                </Box>

                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {channel.description || 'No description available'}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    Timezone:
                  </Typography>
                  <Typography variant="caption">
                    {channel.timezone || 'UTC'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    Channel ID:
                  </Typography>
                  <Typography variant="caption" fontFamily="monospace">
                    {channel._id}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Shows Section */}
            <Card id="shows-section">
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Shows ({shows.length})
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => handleOpenShowDialog()}
                    sx={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      borderRadius: 1.5,
                      px: 2,
                      py: 0.5,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                      },
                    }}
                  >
                    Add Show
                  </Button>
                </Box>

                {shows.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <RadioIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1.5 }} />
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      No shows in this channel yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Create your first show to get started
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={() => handleOpenShowDialog()}
                      sx={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        borderRadius: 1.5,
                        px: 2,
                        py: 0.5,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                        },
                      }}
                    >
                      Add First Show
                    </Button>
                  </Box>
                ) : (
                  <Grid container spacing={1.5}>
                    {shows.map((show) => (
                      <Grid item xs={12} sm={6} md={4} key={show._id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                              transform: 'translateY(-1px)',
                            }
                          }}
                          onClick={() => navigate(`/shows/${show._id}`)}
                        >
                          <CardContent sx={{ p: 1.5 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                              {show.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                              {show.description || 'No description available'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                              <Chip 
                                label={show.status} 
                                size="small" 
                                color={getStatusColor(show.status) as any}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                              {show.frequency && (
                                <Chip 
                                  label={show.frequency} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                            </Box>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/shows/${show._id}`);
                              }}
                              fullWidth
                              sx={{ fontSize: '0.7rem', py: 0.5 }}
                            >
                              View Details
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            {/* Quick Stats */}
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Quick Stats
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <RadioIcon sx={{ mr: 1.5, color: 'secondary.main', fontSize: 20 }} />
                  <Box>
                    <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
                      {shows.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Shows
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<RadioIcon />}
                  size="small"
                  onClick={() => {
                    const showsSection = document.getElementById('shows-section');
                    if (showsSection) {
                      showsSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  disabled={shows.length === 0}
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    borderRadius: 1.5,
                    py: 0.5,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    },
                  }}
                >
                  {shows.length === 0 ? 'No Shows' : 'Manage Shows'}
                </Button>

                {shows.length > 0 && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<VideoLibraryIcon />}
                    size="small"
                    onClick={() => handleOpenShowDialog()}
                    sx={{ mt: 1, borderRadius: 1.5, py: 0.5 }}
                  >
                    Add New Show
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Actions
                </Typography>
                
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenDialog(channel)}
                  size="small"
                  sx={{ mb: 1, borderRadius: 1.5, py: 0.5 }}
                >
                  Edit Channel
                </Button>
                
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  size="small"
                  sx={{ borderRadius: 1.5, py: 0.5 }}
                >
                  Delete Channel
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      </Box>

      {/* Edit Channel Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Channel
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Channel Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={formData.frequency || ''}
                label="Frequency"
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value as any })}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="hourly">Hourly</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Timezone"
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Show Dialog */}
      <Dialog open={openShowDialog} onClose={handleCloseShowDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Create New Show
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Show Name"
                  value={showFormData.name}
                  onChange={(e) => setShowFormData({ ...showFormData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={showFormData.status}
                    onChange={(e) => setShowFormData({ ...showFormData, status: e.target.value as any })}
                    label="Status"
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={showFormData.description}
                  onChange={(e) => setShowFormData({ ...showFormData, description: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Category"
                  value={showFormData.category}
                  onChange={(e) => setShowFormData({ ...showFormData, category: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={showFormData.frequency}
                    onChange={(e) => setShowFormData({ ...showFormData, frequency: e.target.value as any })}
                    label="Frequency"
                  >
                    <MenuItem value="hourly">Hourly</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Timezone"
                  value={showFormData.timezone}
                  onChange={(e) => setShowFormData({ ...showFormData, timezone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={showFormData.isPublic ? 'public' : 'private'}
                    onChange={(e) => setShowFormData({ ...showFormData, isPublic: e.target.value === 'public' })}
                    label="Visibility"
                  >
                    <MenuItem value="public">Public</MenuItem>
                    <MenuItem value="private">Private</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShowDialog}>Cancel</Button>
          <Button onClick={handleCreateShow} variant="contained" disabled={!showFormData.name.trim()}>
            Create Show
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChannelDetail;
